import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_API_URL } from "../utils/constants";

const VideoUploadSlice = createSlice({
    name: "VideoUpload",
    initialState: {
        loader: false,
        error: null,
        success: null,
        uploadedVideos: [],
    },
    reducers: {
        setLoader: (state, action) => {
            state.loader = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setSuccess: (state, action) => {
            state.success = action.payload;
        },
        resetStatus: (state) => {
            state.error = null;
            state.success = null;
        },
        setUploadedVideos: (state, action) => {
            state.uploadedVideos = action.payload;
        },
    },
});

export const {
    setLoader,
    setError,
    setSuccess,
    resetStatus,
    setUploadedVideos,
} = VideoUploadSlice.actions;

export default VideoUploadSlice.reducer;

const base64ToFile = (base64, filename) => {
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
};

export function uploadVideosWithMetadata(payload) {
    return async (dispatch) => {
        dispatch(setLoader(true));
        dispatch(resetStatus());

        try {
            const { formData, videos } = payload;

            const sanitize = (text) =>
                text
                    ?.toLowerCase()
                    .replace(/\s+/g, "_")
                    .replace(/[^a-z0-9_]/g, "");

            const cleanSpeaker = sanitize(formData.speakerName || "speaker");
            const cleanCategory = sanitize(formData.category || "category");

            const uploadedFiles = [];

            for (let i = 0; i < videos.length; i++) {
                const video = videos[i];

                if (!video.file) {
                    console.warn("Skipping invalid video:", video);
                    continue;
                }

                const originalName = video?.fileName;
                const newFileName = `igniteVideos/${cleanCategory}/${cleanSpeaker}/${originalName}`;

                const renamedFile = new File([video.file], originalName, {
                    type: video.file.type,
                });

                // =========================
                // 🎥 UPLOAD VIDEO
                // =========================
                const { data } = await axios.get(`${BASE_API_URL}/generateSasToken`, {
                    params: { filename: newFileName },
                    headers: {
                        Authorization: localStorage.getItem("token"),
                    },
                });

                const sasUrl = data.sasUrl;

                await axios.put(sasUrl, renamedFile, {
                    headers: {
                        "x-ms-blob-type": "BlockBlob",
                        "Content-Type": renamedFile.type,
                    },
                });

                const cleanVideoUrl = sasUrl.split("?")[0];

                // =========================
                // 🖼️ UPLOAD THUMBNAIL
                // =========================
                let thumbnailUrl = null;

                if (video.thumbnailFile || (video.thumbnail && video.thumbnail.startsWith("data:"))) {
                    try {
                        const thumbFile = video.thumbnailFile
                            ? video.thumbnailFile
                            : base64ToFile(video.thumbnail, `thumb_${originalName}.png`);

                        const thumbPath = `igniteVideos/${cleanCategory}/${cleanSpeaker}/thumb_${originalName}.png`;

                        const { data: thumbData } = await axios.get(
                            `${BASE_API_URL}/generateSasToken`,
                            {
                                params: { filename: thumbPath },
                                headers: {
                                    Authorization: localStorage.getItem("token"),
                                },
                            }
                        );

                        await axios.put(thumbData.sasUrl, thumbFile, {
                            headers: {
                                "x-ms-blob-type": "BlockBlob",
                                "Content-Type": thumbFile.type,
                            },
                        });

                        thumbnailUrl = thumbData.sasUrl.split("?")[0];
                    } catch (err) {
                        console.log("Thumbnail upload failed", err);
                    }
                }

                // =========================
                // 📦 PUSH FINAL DATA
                // =========================
                uploadedFiles.push({
                    fileName: originalName,
                    videoUrl: cleanVideoUrl,
                    thumbnailUrl, // ✅ now valid
                    duration: video.duration,
                    clipTitle: video.clipTitle,
                    clipDescription: video.clipDescription,
                });
            }

            let speakerImageUrl = null;

            if (formData.speakerImage) {
                const imgPath = `igniteVideos/${cleanCategory}/${cleanSpeaker}/speaker_${cleanSpeaker}.png`;

                const { data: imgData } = await axios.get(`${BASE_API_URL}/generateSasToken`, {
                    params: { filename: imgPath },
                    headers: { Authorization: localStorage.getItem("token") },
                });

                await axios.put(imgData.sasUrl, formData.speakerImage, {
                    headers: {
                        "x-ms-blob-type": "BlockBlob",
                        "Content-Type": formData.speakerImage.type,
                    },
                });

                speakerImageUrl = imgData.sasUrl.split("?")[0]; // clean URL — gets SAS on fetch
            }

            // API 2
            const payloadToSend = {
                ...formData,
                videos: uploadedFiles,
                speakerImageUrl
            };

            console.log("videos", uploadedFiles);

            const res = await axios.post(
                `${BASE_API_URL}/saveVideoDetails`,
                payloadToSend,
                {
                    headers: {
                        Authorization: localStorage.getItem("token"),
                        "Content-Type": "application/json",
                    },
                }
            );

            dispatch(setUploadedVideos(uploadedFiles));
            dispatch(setSuccess(res.data.message || "Videos uploaded successfully"));

        } catch (err) {
            console.error("UPLOAD ERROR:", err);

            dispatch(
                setError(
                    err.response?.data?.message || "Upload failed. Please try again."
                )
            );
        } finally {
            dispatch(setLoader(false));
        }
    };
}

export function fetchVideos() {
    return async (dispatch) => {
        try {
            dispatch(setLoader(true));

            const res = await axios.get(`${BASE_API_URL}/getVideos`, {
                headers: {
                    Authorization: localStorage.getItem("token"),
                },
            });

            console.log("=== API response from getVideos ===", res.data);

            dispatch(setUploadedVideos(res.data.data));

        } catch (err) {
            dispatch(setError("Failed to fetch videos"));
        } finally {
            dispatch(setLoader(false));
        }
    };
}

